import { zodResolver } from '@hookform/resolvers/zod';
import {
  RECORD_REFERENCE_TYPE_LABEL_MAX_LENGTH,
  updateRecordReferenceTypeSchema,
  type RecordReferenceType,
  type UpdateRecordReferenceTypeInput,
} from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { Controller, useForm } from 'react-hook-form';

export interface RecordReferenceTypeDialogProps {
  open: boolean;
  type: RecordReferenceType;
  error?: string | null;
  busy?: boolean;
  onSubmit: (label: string) => void;
  onClose: () => void;
}

/** Nuova etichetta di un tipo di riferimento: la chiave resta, i ticket già ricevuti non cambiano (DEC-014). */
export function RecordReferenceTypeDialog({
  open,
  type,
  error = null,
  busy = false,
  onSubmit,
  onClose,
}: RecordReferenceTypeDialogProps) {
  const { control, handleSubmit } = useForm<UpdateRecordReferenceTypeInput>({
    resolver: zodResolver(updateRecordReferenceTypeSchema),
    defaultValues: { label: type.label },
  });

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="record-reference-type-dialog-title"
    >
      <form onSubmit={handleSubmit((values) => onSubmit(values.label))} noValidate>
        <DialogTitle id="record-reference-type-dialog-title">Etichetta di «{type.key}»</DialogTitle>
        <DialogContent>
          <Stack spacing={4} sx={{ pt: 1 }}>
            <DialogContentText>
              La chiave non cambia: il prodotto continua a mandarla com&apos;è. La nuova etichetta vale per le
              segnalazioni successive; i ticket già ricevuti conservano quella con cui sono nati.
            </DialogContentText>
            <Controller
              name="label"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Etichetta"
                  required
                  autoFocus
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  slotProps={{ htmlInput: { maxLength: RECORD_REFERENCE_TYPE_LABEL_MAX_LENGTH } }}
                />
              )}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            Annulla
          </Button>
          <Button type="submit" variant="contained" disabled={busy}>
            Salva
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
